import { useEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { PButton } from "../ui/PButton";
import { PrimeGlyph } from "../ui/PrimeGlyph";
import { appendTranscript, Dictation, unavailableReason, type DictationState } from "./dictation";

// PORT OF `web-speech-to-text`: A TOP-RIGHT ROW OF STATUS ("WAITING FOR MICROPHONE...",
// "TRANSCRIBING...") AND THE LARGE ROUND MICROPHONE / STOP BUTTON, THEN THE BOLD LABEL, THE
// TEXTAREA, AND `text-xs` LINES FOR RECORDING, ERRORS (WITH "ALLOW MICROPHONE" WHEN ACCESS WAS
// DENIED), WHY DICTATION IS UNAVAILABLE, AND THE HELPER TEXT. `onBusyChange` LETS THE CALLER
// DISABLE ITS SUBMIT WHILE A TRANSCRIPT IS STILL COMING; `cancelToken` CHANGING ABANDONS
// DICTATION (A HIDDEN TAB OR COLLAPSED PANEL MUST NOT KEEP RECORDING OFF SCREEN).
export function DictationField(props: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  rows: number;
  maxLength: number;
  helperText: string;
  onBusyChange?: (busy: boolean) => void;
  cancelToken?: unknown;
  disabled?: boolean;
}) {
  const { value, onChange, label, rows, maxLength, helperText, onBusyChange, cancelToken, disabled } = props;
  const [state, setState] = useState<DictationState | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const dictation = useRef<Dictation | null>(null);

  useEffect(() => {
    const d = new Dictation(setState, (text) => onChangeRef.current(appendTranscript(valueRef.current, text, maxLength)));
    dictation.current = d;
    setState(d.current);
    return () => d.dispose();
  }, [maxLength]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const busy = state?.isBusy ?? false;
  useEffect(() => onBusyChange?.(busy), [busy, onBusyChange]);

  // THE FIRST RUN IS THE MOUNT, NOT A HIDE
  const firstToken = useRef(true);
  useEffect(() => {
    if (firstToken.current) {
      firstToken.current = false;
      return;
    }
    dictation.current?.cancel();
  }, [cancelToken]);

  const s = state;
  const reason = s ? unavailableReason(s) : null;
  return (
    <div>
      <div className="flex flex-row align-items-center justify-content-end gap-2">
        {s?.isStarting && (
          <div className="flex align-items-center gap-2 text-sm text-500">
            <span className="pi-spin" style={{ display: "inline-flex" }}>
              <PrimeGlyph name="spinner" />
            </span>
            <span>Waiting for microphone...</span>
          </div>
        )}
        {(s?.isStopping || s?.isTranscribing) && (
          <div className="flex align-items-center gap-2 text-sm text-500">
            <span className="pi-spin" style={{ display: "inline-flex" }}>
              <PrimeGlyph name="spinner" />
            </span>
            <span>Transcribing...</span>
          </div>
        )}
        {s?.isAvailable && !s.isBusy && (
          <div>
            <Tooltip title="Start dictation" placement="top">
              <span>
                <PButton icon="microphone" rounded variant="text" size="lg" ariaLabel="Start dictation" disabled={disabled} onClick={() => void dictation.current?.start()} />
              </span>
            </Tooltip>
          </div>
        )}
        {s?.isRecording && (
          <div className="fadein animation-duration-1000 animation-iteration-infinite">
            <Tooltip title="Stop dictation and transcribe" placement="top">
              <span>
                <PButton icon="stop-circle" rounded variant="text" severity="danger" size="lg" ariaLabel="Stop dictation and transcribe" onClick={() => dictation.current?.stop()} />
              </span>
            </Tooltip>
          </div>
        )}
      </div>

      <div className="font-bold">{label}</div>
      <textarea
        className="p-inputtext w-full h-full mt-1"
        style={{ resize: "none" }}
        rows={rows}
        maxLength={5000}
        aria-label={label.replace(/:$/, "")}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />

      {s?.isRecording && (
        <div className="mt-1 text-xs text-primary flex align-items-center gap-1">
          <PrimeGlyph name="circle-fill" size="0.5rem" />
          <span>Recording... press stop to transcribe.</span>
        </div>
      )}
      {s?.error && (
        <div className="mt-1 text-xs text-red-500">
          <LineIcon name="exclamation-circle" />
          {s.error}
          {s.permissionDenied && s.isSupported && (
            <button type="button" className="hover-link text-primary ml-2" onClick={() => void dictation.current?.requestPermission()} style={{ border: 0, background: "none", padding: 0, font: "inherit" }}>
              Allow microphone
            </button>
          )}
        </div>
      )}
      {s && !s.isAvailable && !s.isRecording && reason && (
        <div className="mt-1 text-xs text-500">
          <LineIcon name="info-circle" />
          {reason}
        </div>
      )}
      <div className="mt-1 text-xs text-gray-500">
        {helperText} Max {maxLength} characters.
      </div>
    </div>
  );
}

// AN INLINE `pi` ICON IN A `text-xs` LINE. THE PRIMEICONS FONT'S METRICS MAKE THAT LINE 14.75PX
// TALL IN ANGULAR RATHER THAN 10.5PX, SO THE SVG STAND-IN HOLDS THE LINE TO THE SAME HEIGHT
function LineIcon({ name }: { name: "info-circle" | "exclamation-circle" }) {
  return (
    <span className="mr-1" style={{ display: "inline-flex", alignItems: "center", height: "14.75px", verticalAlign: "top" }}>
      <PrimeGlyph name={name} size="0.75rem" />
    </span>
  );
}
